import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Heart, AlertTriangle, CheckCircle, ChevronRight, RotateCcw, Printer, Activity, Stethoscope, ShieldAlert, Clock, FileText, ChevronLeft, HeartPulse, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import HealthSimulation from '@/components/health/HealthSimulation';
import TeachingGuide from '@/components/health/TeachingGuide';
import CPRQuickGuide from '@/components/health/CPRQuickGuide';
import EpiPenQuickGuide from '@/components/health/EpiPenQuickGuide';
import DemoCall from '@/components/health/DemoCall';
import { DEMO_CALLS } from '@/components/health/demoCallScripts';
import { QRCodeSVG } from 'qrcode.react';
import InfoGatheringPhase from '@/components/health/phases/InfoGatheringPhase';
import PreArrivalPhase, { emptyPreArrival, buildPreArrivalPrintHtml, SCENE_CHECKS, formatChrono } from '@/components/health/PreArrivalPhase';
import { CallNotesPanel, CallNotesCard, CallFlowCard, buildPhasesHtml, buildNotesHtml } from '@/components/health/callReport';

const PRIORITY_CONFIG = {
  1: { label: 'PRIORITÉ 1', title: 'Arrêt cardiaque suspecté', color: '#DC2626', bg: '#FEE2E2', icon: ShieldAlert },
  2: { label: 'PRIORITÉ 2', title: 'Douleur thoracique critique', color: '#F59E0B', bg: '#FEF3C7', icon: AlertTriangle },
  4: { label: 'PRIORITÉ 4', title: 'Évaluation en cours / stable', color: '#22C55E', bg: '#DCFCE7', icon: CheckCircle },
};

// MPDS protocol categories (with colors + icons). Keep in sync with PROTOCOL_META below.
const CATEGORY_CONFIG = {
  cardiovasculaire: { label: 'Cardiovasculaire', color: '#DC2626' },
  respiratoire: { label: 'Respiratoire', color: '#3B82F6' },
  neurologique: { label: 'Neurologique', color: '#8B5CF6' },
  metabolique: { label: 'Métabolique', color: '#10B981' },
  toxique: { label: 'Toxique', color: '#F59E0B' },
  traumatique: { label: 'Traumatique', color: '#F4621C' },
  gyneco_obstetrique: { label: 'Gynéco-obstétrique', color: '#EC4899' },
  psychosocial: { label: 'Psychosocial', color: '#0EA5E9' },
  environnemental: { label: 'Environnemental', color: '#06B6D4' },
  general: { label: 'Général', color: '#6B7280' },
};

const PROTOCOL_META = {
  // Cardiovasculaire
  douleur_thoracique: { category: 'cardiovasculaire', icon: Heart, gradient: 'linear-gradient(135deg, #DC2626, #F59E0B)' },
  avc_suspecte: { category: 'cardiovasculaire', icon: Heart, gradient: 'linear-gradient(135deg, #DC2626, #8B5CF6)' },
  // Respiratoire
  difficulte_respiratoire: { category: 'respiratoire', icon: Activity, gradient: 'linear-gradient(135deg, #3B82F6, #06B6D4)' },
  etouffement: { category: 'respiratoire', icon: AlertTriangle, gradient: 'linear-gradient(135deg, #3B82F6, #DC2626)' },
  // Neurologique
  inconscience: { category: 'neurologique', icon: Activity, gradient: 'linear-gradient(135deg, #8B5CF6, #6B7280)' },
  convulsions: { category: 'neurologique', icon: Activity, gradient: 'linear-gradient(135deg, #8B5CF6, #DC2626)' },
  maux_tete: { category: 'neurologique', icon: Activity, gradient: 'linear-gradient(135deg, #8B5CF6, #3B82F6)' },
  // Métabolique
  diabete: { category: 'metabolique', icon: Activity, gradient: 'linear-gradient(135deg, #10B981, #3B82F6)' },
  fievre: { category: 'metabolique', icon: Activity, gradient: 'linear-gradient(135deg, #10B981, #F59E0B)' },
  reaction_allergique: { category: 'metabolique', icon: AlertTriangle, gradient: 'linear-gradient(135deg, #10B981, #DC2626)' },
  // Toxique
  overdose_empoisonnement: { category: 'toxique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F59E0B, #DC2626)' },
  monoxyde_carbone: { category: 'toxique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F59E0B, #6B7280)' },
  // Traumatique
  morsures: { category: 'traumatique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F4621C, #DC2626)' },
  hemorragie_majeure: { category: 'traumatique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F4621C, #DC2626)' },
  noyade: { category: 'traumatique', icon: Activity, gradient: 'linear-gradient(135deg, #06B6D4, #3B82F6)' },
  pendaison: { category: 'traumatique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F4621C, #6B7280)' },
  electrisation: { category: 'traumatique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F4621C, #F59E0B)' },
  plaies: { category: 'traumatique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F4621C, #DC2626)' },
  brulures: { category: 'traumatique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F4621C, #F59E0B)' },
  chute: { category: 'traumatique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F4621C, #6B7280)' },
  trauma_cranien: { category: 'traumatique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F4621C, #8B5CF6)' },
  trauma_penetrant: { category: 'traumatique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F4621C, #DC2626)' },
  accident_routier: { category: 'traumatique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F4621C, #3B82F6)' },
  fractures: { category: 'traumatique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F4621C, #6B7280)' },
  agression: { category: 'traumatique', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #F4621C, #DC2626)' },
  // Général
  personne_malade: { category: 'general', icon: Activity, gradient: 'linear-gradient(135deg, #6B7280, #3B82F6)' },
  douleur_abdominale: { category: 'general', icon: Activity, gradient: 'linear-gradient(135deg, #6B7280, #F59E0B)' },
  mal_de_dos: { category: 'general', icon: Activity, gradient: 'linear-gradient(135deg, #6B7280, #F4621C)' },
  // Gynéco-obstétrique
  problemes_gyneco: { category: 'gyneco_obstetrique', icon: Heart, gradient: 'linear-gradient(135deg, #EC4899, #DC2626)' },
  grossesse_accouchement: { category: 'gyneco_obstetrique', icon: Heart, gradient: 'linear-gradient(135deg, #EC4899, #8B5CF6)' },
  // Psychosocial
  crise_psychiatrique: { category: 'psychosocial', icon: ShieldAlert, gradient: 'linear-gradient(135deg, #0EA5E9, #8B5CF6)' },
  // Environnemental
  exposition_froid_chaleur: { category: 'environnemental', icon: AlertTriangle, gradient: 'linear-gradient(135deg, #06B6D4, #F59E0B)' },
};

// Phase component for open question (Phase 2)
const OpenQuestionPhase = ({ phase, initial, onComplete }) => {
  const [noted, setNoted] = useState(Array.isArray(initial) ? initial : []);
  return (
    <div data-testid={`phase-${phase.id}`}>
      <div className="p-3 rounded-lg mb-3" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
        <p className="text-sm font-medium text-blue-800 italic">"{phase.question}"</p>
      </div>
      <p className="text-xs text-gray-500 mb-2">Cochez les éléments mentionnés par l'appelant :</p>
      <div className="grid grid-cols-2 gap-1.5">
        {phase.elements_recherches.map(el => (
          <label key={el} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-gray-50 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={noted.includes(el)}
              onChange={() => setNoted(prev => prev.includes(el) ? prev.filter(x => x !== el) : [...prev, el])}
              className="rounded"
            />
            <span className={noted.includes(el) ? 'font-semibold text-gray-800' : 'text-gray-600'}>{el}</span>
          </label>
        ))}
      </div>
      <button onClick={() => onComplete(noted)} className="w-full mt-3 py-2.5 rounded-lg font-semibold text-white" style={{ backgroundColor: '#002768' }} data-testid="phase-next-btn">
        Continuer <ChevronRight className="inline w-4 h-4 ml-1" />
      </button>
    </div>
  );
};

// Phase component for decision (Phase 3, 4, 5, 8)
const DecisionPhase = ({ phase, selected, onAnswer }) => (
  <div data-testid={`phase-${phase.id}`}>
    {phase.critical_question && (
      <div className="p-2 rounded-lg mb-3 flex items-center gap-2" style={{ backgroundColor: '#FEF3C7' }}>
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="text-xs font-semibold text-amber-700">Question critique</span>
      </div>
    )}
    <div className="p-3 rounded-lg mb-3" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
      <p className="text-sm font-medium text-blue-800 italic">"{phase.question}"</p>
    </div>
    {phase.elements_recherches && (
      <div className="mb-3 text-xs text-gray-500">
        <p className="font-semibold mb-1">Éléments à surveiller :</p>
        <div className="flex flex-wrap gap-1">
          {phase.elements_recherches.map(el => (
            <span key={el} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{el}</span>
          ))}
        </div>
      </div>
    )}
    <div className="space-y-2">
      {phase.answers.map(answer => (
        <button
          key={answer.id}
          onClick={() => onAnswer(answer)}
          className="w-full text-left p-3 rounded-lg border-2 transition-all hover:shadow-md"
          style={{
            borderColor: selected === answer.label ? '#2E6CF5' : answer.alert ? '#FCA5A5' : '#E5E7EB',
            backgroundColor: selected === answer.label ? '#EFF6FF' : answer.alert ? '#FEF2F2' : 'white',
          }}
          data-testid={`answer-${answer.id}`}
        >
          <span className="font-semibold text-sm">{answer.label}</span>
          {selected === answer.label && <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">✓ Votre réponse</span>}
          {answer.note && <p className="text-xs text-gray-500 mt-0.5">{answer.note}</p>}
          {answer.alert && <span className="inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">ALERTE</span>}
        </button>
      ))}
    </div>
  </div>
);

// Phase component for multi questions (Phase 6, 7, 9)
const MultiQuestionPhase = ({ phase, initial, onComplete }) => {
  const init = (initial && typeof initial === 'object' && !Array.isArray(initial)) ? initial : {};
  const [answers, setAnswers] = useState(() => {
    const { pain_scale, ...rest } = init;
    return rest;
  });
  const [painScale, setPainScale] = useState(init.pain_scale != null ? Number(init.pain_scale) : 5);
  const allAnswered = phase.questions.every(q => q.type === 'scale' || (q.type === 'open_text' ? (answers[q.id] || '').trim() !== '' : answers[q.id] != null));
  const isRedAnswer = (q, v) => (q.red_flag && v === 'oui') || (q.type === 'multi_choice' && (q.red_flag_options || []).includes(v));
  const redFlags = phase.questions.filter(q => isRedAnswer(q, answers[q.id])).map(q => q.question);

  return (
    <div className="space-y-3" data-testid={`phase-${phase.id}`}>
      {phase.questions.map(q => (
        <div key={q.id} className="p-3 rounded-lg border" style={{ borderColor: isRedAnswer(q, answers[q.id]) ? '#FCA5A5' : '#E5E7EB', backgroundColor: isRedAnswer(q, answers[q.id]) ? '#FEF2F2' : 'white' }}>
          <p className="text-sm font-medium text-gray-700 mb-2">
            {q.red_flag && <ShieldAlert className="inline w-3.5 h-3.5 mr-1 text-red-500" />}
            "{q.question}"
          </p>
          {q.note && <p className="text-xs text-gray-400 mb-2">{q.note}</p>}
          {q.type === 'scale' ? (
            <div>
              <div className="flex items-center gap-3">
                <input type="range" min="0" max="10" value={painScale} onChange={e => setPainScale(Number(e.target.value))} className="flex-1" />
                <span className="text-lg font-bold w-8 text-center" style={{ color: painScale >= 7 ? '#DC2626' : painScale >= 4 ? '#F59E0B' : '#22C55E' }}>{painScale}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Aucune</span><span>Modérée</span><span>Sévère</span>
              </div>
            </div>
          ) : q.type === 'multi_choice' ? (
            <div className="flex flex-wrap gap-2">
              {(q.options || []).map(opt => {
                const isSelected = answers[q.id] === opt;
                const isRed = isSelected && (q.red_flag_options || []).includes(opt);
                return (
                  <button
                    key={opt}
                    onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))}
                    className="px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-all"
                    style={{
                      borderColor: isRed ? '#DC2626' : isSelected ? '#2E6CF5' : '#E5E7EB',
                      backgroundColor: isRed ? '#FEE2E2' : isSelected ? '#EFF6FF' : 'white',
                      color: isRed ? '#DC2626' : isSelected ? '#2E6CF5' : '#6B7280',
                    }}
                    data-testid={`answer-${q.id}-${opt}`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          ) : q.type === 'open_text' ? (
            <input
              type="text"
              value={answers[q.id] || ''}
              onChange={e => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
              placeholder="Notez la réponse de l'appelant..."
              className="w-full px-3 py-2 rounded-lg border-2 text-sm focus:outline-none transition-colors"
              style={{ borderColor: (answers[q.id] || '').trim() ? '#2E6CF5' : '#E5E7EB' }}
              data-testid={`answer-${q.id}-text`}
            />
          ) : (
            <div className="flex gap-2">
              {(q.type === 'yes_no_unknown' ? ['oui', 'non', 'inconnue'] : ['oui', 'non']).map(val => {
                const labels = { oui: q.yes_label || 'Oui', non: q.no_label || 'Non', inconnue: 'Inconnue' };
                const isSelected = answers[q.id] === val;
                const isRedActive = val === 'oui' && q.red_flag && isSelected;
                const isUnknown = val === 'inconnue' && isSelected;
                return (
                  <button
                    key={val}
                    onClick={() => setAnswers(prev => ({ ...prev, [q.id]: val }))}
                    className="flex-1 py-2 rounded-lg text-sm font-semibold border-2 transition-all"
                    style={{
                      borderColor: isRedActive ? '#DC2626' : isUnknown ? '#F59E0B' : isSelected ? '#2E6CF5' : '#E5E7EB',
                      backgroundColor: isRedActive ? '#FEE2E2' : isUnknown ? '#FEF3C7' : isSelected ? '#EFF6FF' : 'white',
                      color: isRedActive ? '#DC2626' : isUnknown ? '#92400E' : isSelected ? '#2E6CF5' : '#6B7280',
                    }}
                    data-testid={`answer-${q.id}-${val}`}
                  >
                    {labels[val]}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
      <button
        onClick={() => onComplete({ ...answers, pain_scale: painScale }, redFlags)}
        disabled={!allAnswered}
        className="w-full mt-2 py-2.5 rounded-lg font-semibold text-white disabled:opacity-40 transition-all"
        style={{ backgroundColor: '#002768' }}
        data-testid="phase-next-btn"
      >
        Continuer <ChevronRight className="inline w-4 h-4 ml-1" />
      </button>
    </div>
  );
};

// Phase component for comparison (Phase 10)
const ComparisonPhase = ({ phase, selected, onAnswer }) => (
  <div data-testid={`phase-${phase.id}`}>
    <div className="grid grid-cols-2 gap-3 mb-4">
      <div className="p-3 rounded-lg" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }}>
        <h4 className="text-xs font-bold text-amber-800 mb-2">Symptômes d'anxiété</h4>
        <ul className="space-y-1">
          {phase.anxiety_symptoms.map(s => (
            <li key={s} className="text-xs text-amber-700 flex items-start gap-1">
              <span className="mt-0.5">•</span>{s}
            </li>
          ))}
        </ul>
      </div>
      <div className="p-3 rounded-lg" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }}>
        <h4 className="text-xs font-bold text-red-800 mb-2">Drapeaux rouges cardiaques</h4>
        <ul className="space-y-1">
          {phase.red_flags_cardiac.map(s => (
            <li key={s} className="text-xs text-red-700 flex items-start gap-1">
              <span className="mt-0.5">•</span>{s}
            </li>
          ))}
        </ul>
      </div>
    </div>
    <p className="text-sm font-medium text-gray-700 mb-3 italic">"{phase.question}"</p>
    <div className="space-y-2">
      {phase.answers.map(a => (
        <button key={a.id} onClick={() => onAnswer(a)} className="w-full text-left p-3 rounded-lg border-2 transition-all hover:shadow-md" style={{ borderColor: selected === a.label ? '#2E6CF5' : a.id === 'cardiaque' ? '#FCA5A5' : '#FCD34D', backgroundColor: selected === a.label ? '#EFF6FF' : a.id === 'cardiaque' ? '#FEF2F2' : '#FFFBEB' }} data-testid={`answer-${a.id}`}>
          <span className="font-semibold text-sm">{a.label}</span>
          {selected === a.label && <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">✓ Votre réponse</span>}
        </button>
      ))}
    </div>
  </div>
);

// Priority alert component
const PriorityAlert = ({ priority, protocol, onContinue }) => {
  const config = PRIORITY_CONFIG[priority];
  const pData = protocol?.priorities?.[String(priority)];
  if (!config || !pData) return null;
  const Icon = config.icon;

  return (
    <div className="space-y-4 animate-in fade-in" data-testid="priority-alert">
      <div className="p-4 rounded-xl border-2" style={{ borderColor: config.color, backgroundColor: config.bg }}>
        <div className="flex items-center gap-2 mb-3">
          <Icon className="w-6 h-6" style={{ color: config.color }} />
          <div>
            <span className="text-xs font-bold" style={{ color: config.color }}>{config.label}</span>
            <h3 className="text-lg font-bold" style={{ color: config.color }}>{config.title}</h3>
          </div>
        </div>

        <div className="mb-3">
          <h4 className="text-sm font-bold text-gray-700 mb-1">Ressources à déployer :</h4>
          <ul className="space-y-1">
            {pData.resources.map(r => (
              <li key={r} className="text-sm flex items-center gap-2">
                <CheckCircle className="w-3.5 h-3.5" style={{ color: config.color }} />
                {r}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-sm font-bold text-gray-700 mb-1">Consignes pré-arrivée :</h4>
          <ol className="space-y-1 list-decimal list-inside">
            {pData.pre_arrival.map(c => (
              <li key={c} className="text-sm text-gray-600">{c}</li>
            ))}
          </ol>
        </div>
      </div>
      {priority !== 1 ? (
        <button onClick={onContinue} className="w-full py-2.5 rounded-lg font-semibold text-white" style={{ backgroundColor: '#002768' }} data-testid="continue-after-priority">
          Continuer l'évaluation <ChevronRight className="inline w-4 h-4 ml-1" />
        </button>
      ) : (
        <button onClick={onContinue} className="w-full py-2.5 rounded-lg font-semibold text-white flex items-center justify-center gap-2" style={{ backgroundColor: '#DC2626' }} data-testid="continue-after-priority-1">
          🚨 Ambulance envoyée — Continuer le questionnement <ChevronRight className="inline w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// Summary component
const SessionSummary = ({ protocol, allAnswers, redFlags, priority, duration, preArrival, p1Seconds, callNotes, phaseTimes, onPrint, onReset, onAutoEval }) => {
  const pConfig = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG[4];
  const pData = protocol?.priorities?.[String(priority)];
  const totalConsignes = (protocol?.priorities?.['1']?.pre_arrival || []).length;
  const givenInstr = preArrival ? Object.entries(preArrival.instructions || {}).sort((a, b) => a[1] - b[1]) : [];
  const givenScene = preArrival ? Object.entries(preArrival.scene || {}).sort((a, b) => a[1] - b[1]) : [];
  const infoLabels = { age: 'Âge exact', medications: 'Médicaments', allergies: 'Allergies', antidote: 'Naloxone / EpiPen', notes: 'Notes' };
  const infoEntries = preArrival
    ? Object.entries(infoLabels).map(([k, label]) => ({ label, value: (preArrival.info?.[k] || '').trim() })).filter(e => e.value)
    : [];

  // Phase 1 (info_gathering) — render filled-in fields if any (RAO context noted by student)
  const phase1 = protocol?.phases?.find(p => p.type === 'info_gathering');
  const phase1Entries = (phase1?.fields || [])
    .map(f => ({ label: f.label, value: (allAnswers[f.id] || '').trim() }))
    .filter(e => e.value);

  return (
    <div className="space-y-4" data-testid="session-summary">
      <div className="p-4 rounded-xl border-2" style={{ borderColor: pConfig.color, backgroundColor: pConfig.bg }}>
        <h3 className="text-lg font-bold" style={{ color: pConfig.color }}>{pConfig.label} — {pConfig.title}</h3>
      </div>

      {phase1Entries.length > 0 && (
        <Card className="border-blue-200 bg-blue-50" data-testid="summary-rao-info">
          <CardContent className="pt-4">
            <h4 className="text-sm font-bold text-blue-800 mb-2">Informations RAO notées par l'étudiant</h4>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
              {phase1Entries.map(e => (
                <div key={e.label} className="flex flex-col">
                  <dt className="text-[11px] uppercase tracking-wide text-blue-700/70">{e.label}</dt>
                  <dd className="text-sm text-blue-900 font-medium">{e.value}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      )}

      {redFlags.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <h4 className="text-sm font-bold text-red-700 mb-2">Drapeaux rouges détectés ({redFlags.length})</h4>
            <ul className="space-y-1">
              {redFlags.map((f, i) => (
                <li key={i} className="text-sm text-red-600 flex items-center gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0" />{f}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {preArrival && p1Seconds != null && (
        <Card className="border-orange-200 bg-orange-50" data-testid="summary-pre-arrival">
          <CardContent className="pt-4">
            <h4 className="text-sm font-bold text-orange-800 mb-2">🚑 Volet P1 — Consignes pré-arrivée &amp; paramédics</h4>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div className="p-2 rounded-lg bg-white border text-center">
                <div className="text-[10px] uppercase text-gray-500">Délai P1 → fin</div>
                <div className="text-sm font-bold text-orange-700 font-mono">{formatChrono(p1Seconds)}</div>
              </div>
              <div className="p-2 rounded-lg bg-white border text-center">
                <div className="text-[10px] uppercase text-gray-500">Consignes données</div>
                <div className="text-sm font-bold text-orange-700">{givenInstr.length} / {totalConsignes}</div>
              </div>
              <div className="p-2 rounded-lg bg-white border text-center">
                <div className="text-[10px] uppercase text-gray-500">Sécurisation</div>
                <div className="text-sm font-bold text-orange-700">{givenScene.length} / {SCENE_CHECKS.length}</div>
              </div>
            </div>
            {givenInstr.length > 0 && (
              <ul className="space-y-0.5 mb-2">
                {givenInstr.map(([label, at]) => (
                  <li key={label} className="text-xs text-orange-900 flex items-center gap-1.5">
                    <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />{label}
                    <span className="text-[10px] text-gray-400 font-mono ml-auto">{formatChrono(at)}</span>
                  </li>
                ))}
              </ul>
            )}
            {infoEntries.length > 0 && (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {infoEntries.map(e => (
                  <div key={e.label} className="flex flex-col">
                    <dt className="text-[10px] uppercase tracking-wide text-orange-700/70">{e.label}</dt>
                    <dd className="text-xs text-orange-900 font-medium">{e.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </CardContent>
        </Card>
      )}

      <CallFlowCard protocol={protocol} allAnswers={allAnswers} phaseTimes={phaseTimes} />

      <CallNotesCard notes={callNotes} />

      {pData && (
        <Card className="border-0 glass">
          <CardContent className="pt-4">
            <h4 className="text-sm font-bold text-gray-700 mb-2">Ressources à déployer</h4>
            <ul className="space-y-1">{pData.resources.map(r => <li key={r} className="text-sm text-gray-600">• {r}</li>)}</ul>
            <h4 className="text-sm font-bold text-gray-700 mt-3 mb-2">Consignes pré-arrivée</h4>
            <ol className="space-y-1 list-decimal list-inside">{pData.pre_arrival.map(c => <li key={c} className="text-sm text-gray-600">{c}</li>)}</ol>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-2">
        {onAutoEval && (
          <button onClick={onAutoEval} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-white" style={{ backgroundColor: '#7C3AED' }} data-testid="health-auto-eval-btn">
            <Award className="w-4 h-4" /> Évaluation automatique (IA)
          </button>
        )}
        <div className="flex gap-2">
          <button onClick={onPrint} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-white" style={{ backgroundColor: '#002768' }} data-testid="print-summary-btn">
            <Printer className="w-4 h-4" /> Imprimer
          </button>
          <button onClick={onReset} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold border-2 border-gray-300 text-gray-600 hover:bg-gray-50" data-testid="reset-session-btn">
            <RotateCcw className="w-4 h-4" /> Recommencer
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Health page
const Health = ({ studentName, studentNumber, isAdmin = false }) => {
  const navigate = useNavigate();
  const [protocolsList, setProtocolsList] = useState([]);
  const [selectedProtocolId, setSelectedProtocolId] = useState(null);
  const [protocol, setProtocol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [currentPhaseIdx, setCurrentPhaseIdx] = useState(0);
  const [allAnswers, setAllAnswers] = useState({});
  const [redFlags, setRedFlags] = useState([]);
  const [currentPriority, setCurrentPriority] = useState(4);
  const [showPriorityAlert, setShowPriorityAlert] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [mode, setMode] = useState(null); // 'guided' or 'simulation'
  const [showDemo, setShowDemo] = useState(false);
  const [phaseHistory, setPhaseHistory] = useState([]);
  const [p1TriggeredAt, setP1TriggeredAt] = useState(null);
  const [p1EndSeconds, setP1EndSeconds] = useState(null);
  const [preArrival, setPreArrival] = useState(emptyPreArrival());
  const [callNotes, setCallNotes] = useState([]);
  const [phaseTimes, setPhaseTimes] = useState({});
  const [maxReachedIdx, setMaxReachedIdx] = useState(0);
  const contentRef = useRef(null);
  const [ambulanceScenarios, setAmbulanceScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [showScenarioSelect, setShowScenarioSelect] = useState(false);
  const [showAllScenarios, setShowAllScenarios] = useState(false);
  // Admin "preview as student" mode — only used when isAdmin is true
  const [viewAsStudent, setViewAsStudent] = useState(false);

  // Search & category filter state (must be declared before any early return)
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  // Admin-only: teaching guide
  const [teachingGuideId, setTeachingGuideId] = useState(null);
  // CPR Quick Guide modal
  const [showCPRGuide, setShowCPRGuide] = useState(false);
  // EpiPen quick reference (Réaction allergique only)
  const [showEpiPenGuide, setShowEpiPenGuide] = useState(false);

  // Auto-detect cardiac arrest pattern from red flags or current phase content
  const cprAlertActive = redFlags.some(rf => {
    const t = (rf || '').toLowerCase();
    return (
      t.includes('inconscient') || t.includes('ne respire') ||
      t.includes('arrêt') || t.includes('rcr') ||
      t.includes('respiration agonale') || t.includes('gasps')
    );
  });

  // Load list of protocols on mount and whenever admin toggles preview mode
  useEffect(() => {
    const params = {};
    if (studentNumber) params.student_number = studentNumber;
    if (isAdmin && viewAsStudent) params.as_student = true;
    setLoading(true);
    axios.get(`${API}/health/protocols`, { params })
      .then(resp => setProtocolsList(resp.data || []))
      .catch(() => toast.error('Erreur de chargement des protocoles'))
      .finally(() => setLoading(false));
    // Load ambulance scenarios for simulation mode (only once)
    axios.get(`${API}/scenarios`)
      .then(resp => setAmbulanceScenarios(resp.data.filter(s => s.category === 'ambulance')))
      .catch(() => {});
  }, [viewAsStudent, studentNumber, isAdmin]);

  // Load full protocol when one is selected
  useEffect(() => {
    if (!selectedProtocolId) {
      setProtocol(null);
      return;
    }
    setLoading(true);
    axios.get(`${API}/health/protocols/${selectedProtocolId}`)
      .then(resp => setProtocol(resp.data))
      .catch(() => toast.error('Erreur de chargement du protocole'))
      .finally(() => setLoading(false));
  }, [selectedProtocolId]);

  const startSession = async (selectedMode) => {
    setMode(selectedMode);
    setSessionStartTime(Date.now());
    setCurrentPhaseIdx(0);
    setAllAnswers({});
    setRedFlags([]);
    setCurrentPriority(4);
    setShowPriorityAlert(false);
    setCompleted(false);
    setPhaseHistory([]);
    setP1TriggeredAt(null);
    setP1EndSeconds(null);
    setPreArrival(emptyPreArrival());
    setCallNotes([]);
    setPhaseTimes({});
    setMaxReachedIdx(0);
    try {
      const resp = await axios.post(`${API}/health/sessions`, {
        protocol_id: selectedProtocolId,
        student_name: studentName,
        student_number: studentNumber,
        mode: selectedMode,
      });
      setSessionId(resp.data.session.id);
    } catch {
      toast.error('Erreur lors de la création de la session');
    }
  };

  const getElapsed = useCallback(() => sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0, [sessionStartTime]);

  const recordPhaseTime = useCallback((phaseId) => {
    const at = sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0;
    setPhaseTimes(prev => prev[phaseId] != null ? prev : { ...prev, [phaseId]: at });
  }, [sessionStartTime]);

  const advancePhase = useCallback(() => {
    if (!protocol) return;
    recordPhaseTime(protocol.phases[currentPhaseIdx]?.id);
    const nextIdx = currentPhaseIdx + 1;
    if (nextIdx >= protocol.phases.length) {
      if (currentPriority === 1) {
        // P1 active — unlock the pre-arrival virtual phase instead of ending
        setPhaseHistory(prev => [...prev, currentPhaseIdx]);
        setCurrentPhaseIdx(protocol.phases.length);
        setMaxReachedIdx(prev => Math.max(prev, protocol.phases.length));
        setShowPriorityAlert(false);
        contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        completeSession();
      }
    } else {
      setPhaseHistory(prev => [...prev, currentPhaseIdx]);
      setCurrentPhaseIdx(nextIdx);
      setMaxReachedIdx(prev => Math.max(prev, nextIdx));
      setShowPriorityAlert(false);
      contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [protocol, currentPhaseIdx, currentPriority, recordPhaseTime]);

  const handlePriorityChange = useCallback((newPriority) => {
    if (newPriority && newPriority < currentPriority) {
      setCurrentPriority(newPriority);
      setShowPriorityAlert(true);
    }
  }, [currentPriority]);

  const handleDecisionAnswer = useCallback((answer) => {
    const phase = protocol.phases[currentPhaseIdx];
    setAllAnswers(prev => ({ ...prev, [phase.id]: answer.label }));
    if (answer.priority_change) {
      handlePriorityChange(answer.priority_change);
      if (answer.alert) {
        const flag = phase.question + ' → ' + answer.label;
        setRedFlags(prev => prev.includes(flag) ? prev : [...prev, flag]);
      }
    }
    if (answer.next === 'priority_1') {
      recordPhaseTime(phase.id);
      setCurrentPriority(1);
      setShowPriorityAlert(true);
      setP1TriggeredAt(prev => prev || Date.now());
      const flag = phase.question + ' → ' + answer.label;
      setRedFlags(prev => prev.includes(flag) ? prev : [...prev, flag]);
      // Do NOT complete session — the student decides when to end (Terminer l'appel button).
      // The PriorityAlert modal now shows "Continuer le questionnement" for P1 too.
      return;
    }
    advancePhase();
  }, [protocol, currentPhaseIdx, advancePhase, handlePriorityChange, recordPhaseTime]);

  const handleMultiComplete = useCallback((answers, newRedFlags) => {
    const phase = protocol.phases[currentPhaseIdx];
    setAllAnswers(prev => ({ ...prev, [phase.id]: answers }));
    if (newRedFlags.length > 0) {
      setRedFlags(prev => [...prev, ...newRedFlags.filter(f => !prev.includes(f))]);
      if (currentPriority > 2) {
        setCurrentPriority(2);
        setShowPriorityAlert(true);
      }
    }
    if (!showPriorityAlert) advancePhase();
  }, [protocol, currentPhaseIdx, advancePhase, currentPriority, showPriorityAlert]);

  const handleComparisonAnswer = useCallback((answer) => {
    const phase = protocol.phases[currentPhaseIdx];
    setAllAnswers(prev => ({ ...prev, [phase.id]: answer.label }));
    if (answer.id === 'cardiaque' && currentPriority > 2) {
      setCurrentPriority(2);
    }
    advancePhase();
  }, [protocol, currentPhaseIdx, advancePhase, currentPriority]);

  const completeSession = async () => {
    setCompleted(true);
    const duration = sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0;
    const p1Seconds = p1TriggeredAt ? Math.floor((Date.now() - p1TriggeredAt) / 1000) : null;
    setP1EndSeconds(p1Seconds);
    if (sessionId) {
      await axios.put(`${API}/health/sessions/${sessionId}`, {
        status: 'completed',
        completed_at: new Date().toISOString(),
        duration_seconds: duration,
        answers: allAnswers,
        red_flags_detected: redFlags,
        final_priority: currentPriority,
        p1_triggered_at: p1TriggeredAt ? new Date(p1TriggeredAt).toISOString() : null,
        p1_to_end_seconds: p1Seconds,
        pre_arrival: p1TriggeredAt ? preArrival : null,
        call_notes: callNotes,
        phase_times: phaseTimes,
      }).catch(() => {});
    }
  };

  const printSummary = () => {
    const pConfig = PRIORITY_CONFIG[currentPriority] || PRIORITY_CONFIG[4];
    const pData = protocol?.priorities?.[String(currentPriority)];
    const duration = sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0;
    const formatTime = (s) => Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');

    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Autorisez les popups'); return; }

    const rfHtml = redFlags.length > 0
      ? '<div style="margin-top:16px;padding:12px;border-left:4px solid #DC2626;background:#FEF2F2;border-radius:8px;">'
        + '<h4 style="color:#DC2626;font-size:14px;font-weight:bold;margin-bottom:8px;">Drapeaux rouges détectés (' + redFlags.length + ')</h4>'
        + '<ul style="list-style:none;padding:0;">' + redFlags.map(f => '<li style="font-size:13px;color:#B91C1C;padding:2px 0;">⚠ ' + f + '</li>').join('') + '</ul></div>'
      : '';

    // Phase 1 RAO info — only print if the student filled any fields
    const phase1 = protocol?.phases?.find(p => p.type === 'info_gathering');
    const phase1Entries = (phase1?.fields || [])
      .map(f => ({ label: f.label, value: (allAnswers[f.id] || '').trim() }))
      .filter(e => e.value);
    const escapeHtml = (s) => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const phase1Html = phase1Entries.length > 0
      ? '<div style="margin-top:16px;padding:12px;border-left:4px solid #2E6CF5;background:#EFF6FF;border-radius:8px;">'
        + '<h4 style="color:#1e40af;font-size:14px;font-weight:bold;margin-bottom:8px;">Informations RAO notées par l\'étudiant</h4>'
        + '<table style="width:100%;border-collapse:collapse;font-size:13px;">'
        + phase1Entries.map(e =>
            '<tr><td style="padding:3px 8px 3px 0;color:#1e3a8a;font-weight:600;width:40%;vertical-align:top;">'
            + escapeHtml(e.label) + '</td><td style="padding:3px 0;color:#1e293b;">' + escapeHtml(e.value) + '</td></tr>'
          ).join('')
        + '</table></div>'
      : '';

    const resourcesHtml = pData
      ? '<div style="margin-top:16px;"><h4 style="font-size:14px;font-weight:bold;">Ressources à déployer</h4><ul>' + pData.resources.map(r => '<li style="font-size:13px;">' + r + '</li>').join('') + '</ul>'
        + '<h4 style="font-size:14px;font-weight:bold;margin-top:12px;">Consignes pré-arrivée</h4><ol>' + pData.pre_arrival.map(c => '<li style="font-size:13px;">' + c + '</li>').join('') + '</ol></div>'
      : '';

    const preArrivalHtml = p1TriggeredAt && p1EndSeconds != null
      ? buildPreArrivalPrintHtml(preArrival, p1EndSeconds, (protocol?.priorities?.['1']?.pre_arrival || []).length)
      : '';

    const phasesHtml = buildPhasesHtml(protocol, allAnswers, phaseTimes);
    const notesHtml = buildNotesHtml(callNotes);

    printWindow.document.write('<!DOCTYPE html><html><head><title>Santé — ' + protocol.title + '</title>'
      + '<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Segoe UI,Arial,sans-serif;padding:40px;color:#1e293b;line-height:1.6;}'
      + '.header{text-align:center;margin-bottom:24px;padding-bottom:16px;border-bottom:2px solid #e2e8f0;}'
      + '.meta{display:flex;gap:30px;flex-wrap:wrap;padding:12px 16px;background:#f8fafc;border-radius:8px;margin-bottom:16px;}'
      + '.meta-item{font-size:13px;}</style></head><body>'
      + '<div class="header"><h1 style="font-size:20px;color:#002768;">Protocole Santé — ' + protocol.title + '</h1>'
      + '<p style="font-size:12px;color:#64748b;">Simulateur 911 — Formation répartition médicale</p></div>'
      + '<div class="meta"><div class="meta-item"><strong>Étudiant:</strong> ' + studentName + '</div>'
      + '<div class="meta-item"><strong>Date:</strong> ' + new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + '</div>'
      + '<div class="meta-item"><strong>Mode:</strong> ' + (mode === 'guided' ? 'Guidé' : 'Simulation') + '</div>'
      + '<div class="meta-item"><strong>Durée:</strong> ' + formatTime(duration) + '</div></div>'
      + '<div style="padding:16px;border:2px solid ' + pConfig.color + ';background:' + pConfig.bg + ';border-radius:12px;margin-bottom:16px;">'
      + '<h2 style="font-size:18px;color:' + pConfig.color + ';">' + pConfig.label + ' — ' + pConfig.title + '</h2></div>'
      + phase1Html + notesHtml + rfHtml + phasesHtml + preArrivalHtml + resourcesHtml
      + '<div style="margin-top:24px;text-align:center;font-size:11px;color:#94a3b8;">Simulateur 911 — ' + new Date().toLocaleDateString('fr-FR') + '</div>'
      + '</body></html>');
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  const resetSession = () => {
    setMode(null);
    setSessionId(null);
    setCurrentPhaseIdx(0);
    setAllAnswers({});
    setRedFlags([]);
    setCurrentPriority(4);
    setShowPriorityAlert(false);
    setCompleted(false);
    setSessionStartTime(null);
    setPhaseHistory([]);
    setP1TriggeredAt(null);
    setP1EndSeconds(null);
    setPreArrival(emptyPreArrival());
    setCallNotes([]);
    setPhaseTimes({});
    setMaxReachedIdx(0);
    setSelectedProtocolId(null);
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Chargement...</div>;

  // STEP 1 — Protocol selection (before any mode)
  if (!selectedProtocolId) {
    // Admin teaching guide overrides selection
    if (teachingGuideId) {
      return <TeachingGuide protocolId={teachingGuideId} onExit={() => setTeachingGuideId(null)} />;
    }
    // Compute filtered list — by category and search query
    const norm = s => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const q = norm(searchQuery);
    const filteredProtocols = protocolsList.filter(p => {
      const meta = PROTOCOL_META[p.id] || { category: 'general' };
      if (activeCategory !== 'all' && meta.category !== activeCategory) return false;
      if (!q) return true;
      return norm(p.title).includes(q) || norm(p.subtitle).includes(q) || norm(p.description).includes(q);
    });
    // Available categories — only show pills for categories actually present
    const availableCategories = Array.from(new Set(
      protocolsList.map(p => (PROTOCOL_META[p.id] || { category: 'general' }).category)
    ));
    return (
      <div className="max-w-5xl mx-auto py-8 px-4" data-testid="health-protocol-select">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #DC2626, #F59E0B)' }}>
            <Stethoscope className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: '#002768' }}>Santé — Protocoles MPDS</h1>
          <p className="text-gray-500 text-sm mt-1">Choisissez un déterminant pour commencer</p>
          {isAdmin && (
            <div className="mt-4 inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/70 border shadow-sm" data-testid="admin-preview-toggle">
              <span className="text-xs font-medium text-gray-600">Mode aperçu :</span>
              <button
                type="button"
                onClick={() => setViewAsStudent(false)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${!viewAsStudent ? 'text-white shadow' : 'bg-gray-100 text-gray-500'}`}
                style={!viewAsStudent ? { backgroundColor: '#002768' } : {}}
                data-testid="view-all-button"
              >
                Voir tout (admin)
              </button>
              <button
                type="button"
                onClick={() => setViewAsStudent(true)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${viewAsStudent ? 'text-white shadow' : 'bg-gray-100 text-gray-500'}`}
                style={viewAsStudent ? { backgroundColor: '#DC2626' } : {}}
                data-testid="view-as-student-button"
              >
                Voir comme étudiant
              </button>
              <span className="text-xs text-gray-500">
                {viewAsStudent
                  ? `${protocolsList.length} protocole(s) selon vos groupes`
                  : `${protocolsList.length} protocole(s) — accès total`}
              </span>
            </div>
          )}
        </div>

        {/* Search bar + category filter */}
        <div className="mb-6" data-testid="health-filters">
          <div className="relative mb-3">
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Rechercher un déterminant (titre, sous-titre, description)..."
              className="w-full pl-10 pr-10 py-2.5 rounded-full border border-gray-200 bg-white/80 focus:outline-none focus:border-blue-400 text-sm"
              data-testid="protocol-search-input"
            />
            <FileText className="w-4 h-4 text-gray-400 absolute left-3.5 top-3 pointer-events-none" />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-2 text-gray-400 hover:text-gray-700 text-xs"
                data-testid="clear-search-button"
              >
                Effacer
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              type="button"
              onClick={() => setActiveCategory('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${activeCategory === 'all' ? 'text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              style={activeCategory === 'all' ? { backgroundColor: '#002768' } : {}}
              data-testid="filter-category-all"
            >
              Toutes ({protocolsList.length})
            </button>
            {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
              if (!availableCategories.includes(key)) return null;
              const count = protocolsList.filter(p => (PROTOCOL_META[p.id] || { category: 'general' }).category === key).length;
              const isActive = activeCategory === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveCategory(key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${isActive ? 'text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  style={isActive ? { backgroundColor: cfg.color } : {}}
                  data-testid={`filter-category-${key}`}
                >
                  {cfg.label} ({count})
                </button>
              );
            })}
          </div>
          {(searchQuery || activeCategory !== 'all') && (
            <p className="text-center text-xs text-gray-500 mt-3" data-testid="filter-result-count">
              {filteredProtocols.length} résultat(s)
              {filteredProtocols.length === 0 && (
                <button
                  type="button"
                  onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
                  className="ml-2 underline text-blue-600"
                  data-testid="reset-filters-button"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredProtocols.map(p => {
            const meta = PROTOCOL_META[p.id] || { category: 'general', icon: Activity, gradient: 'linear-gradient(135deg, #6B7280, #3B82F6)' };
            const catCfg = CATEGORY_CONFIG[meta.category] || CATEGORY_CONFIG.general;
            const Icon = meta.icon;
            return (
              <div
                key={p.id}
                className="text-left p-5 rounded-xl border-2 border-gray-200 hover:border-gray-400 transition-all bg-white hover:shadow-md relative"
              >
                <button
                  onClick={() => setSelectedProtocolId(p.id)}
                  className="w-full text-left"
                  data-testid={`select-protocol-${p.id}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: meta.gradient }}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: catCfg.color }}>{catCfg.label}</p>
                      <h3 className="font-bold text-base text-gray-800 leading-tight">{p.title}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{p.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 leading-relaxed">{p.description}</p>
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setTeachingGuideId(p.id); }}
                    className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
                    title="Guide pédagogique pour instructeur"
                    data-testid={`teaching-guide-button-${p.id}`}
                  >
                    📖 Guide
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Autres déterminants à venir prochainement.
        </p>
      </div>
    );
  }

  if (!protocol) return <div className="text-center py-12 text-gray-500">Chargement du protocole...</div>;

  // Demonstration mode — animated model call
  if (showDemo) {
    return <DemoCall protocol={protocol} onExit={() => setShowDemo(false)} />;
  }

  // Simulation mode with selected scenario
  if (selectedScenario) {
    return (
      <HealthSimulation
        protocol={protocol}
        studentName={studentName}
        studentNumber={studentNumber}
        scenario={selectedScenario}
        onExit={() => { setSelectedScenario(null); setMode(null); }}
      />
    );
  }

  // Mode selection
  if (!mode) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4" data-testid="health-page">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #DC2626, #F59E0B)' }}>
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: '#002768' }}>{protocol.title}</h1>
          <p className="text-gray-500 text-sm mt-1">{protocol.subtitle}</p>
        </div>

        <Card className="glass border-0 mb-4">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-2" style={{ color: '#002768' }}>Objectifs du protocole</h3>
            <ul className="space-y-1">
              {protocol.objectives.map(o => (
                <li key={o} className="text-sm text-gray-600 flex items-start gap-2">
                  <CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-500 flex-shrink-0" />{o}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => startSession('guided')}
            className="p-4 rounded-xl border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 transition-all text-left"
            data-testid="start-guided-btn"
          >
            <FileText className="w-6 h-6 text-blue-600 mb-2" />
            <h3 className="font-bold text-sm text-blue-800">Mode Guidé</h3>
            <p className="text-xs text-blue-600 mt-1">Le système vous guide phase par phase. Idéal pour apprendre le protocole.</p>
          </button>
          <button
            onClick={() => { setShowScenarioSelect(true); setShowAllScenarios(false); }}
            className="p-4 rounded-xl border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 transition-all text-left"
            data-testid="start-simulation-btn"
          >
            <Stethoscope className="w-6 h-6 text-amber-600 mb-2" />
            <h3 className="font-bold text-sm text-amber-800">Mode Simulation</h3>
            <p className="text-xs text-amber-600 mt-1">Pratiquez comme en situation réelle. Questionnez et évaluez par vous-même.</p>
          </button>
        </div>

        {/* Mode Démonstration — appel modèle animé */}
        {DEMO_CALLS[selectedProtocolId] && (
          <button
            onClick={() => setShowDemo(true)}
            className="mt-3 w-full p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-all text-left flex items-center gap-3"
            data-testid="start-demo-btn"
          >
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#059669' }}>
              <span className="text-white text-lg">▶</span>
            </div>
            <div>
              <h3 className="font-bold text-sm text-emerald-800">Démonstration — Appel modèle</h3>
              <p className="text-xs text-emerald-600 mt-0.5">Regardez un appel complet se dérouler automatiquement, avec les explications pédagogiques aux moments clés.</p>
            </div>
          </button>
        )}

        {/* QR code — guide mobile + auto-évaluation du déterminant */}
        <Card className="glass border-0 mt-4" data-testid="protocol-qr-card">
          <CardContent className="pt-4 pb-4 flex items-center gap-4">
            <div className="p-2 bg-white rounded-lg border border-gray-200 flex-shrink-0">
              <QRCodeSVG value={`${window.location.origin}/m/guide/${selectedProtocolId}`} size={96} data-testid="protocol-qr-code" />
            </div>
            <div>
              <h3 className="font-semibold text-sm" style={{ color: '#002768' }}>Guide mobile du déterminant</h3>
              <p className="text-xs text-gray-500 mt-1">
                Scannez avec votre téléphone pour consulter le guide d'étude complet et faire le <strong>quiz d'auto-évaluation</strong> de ce déterminant.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Scenario selection modal for simulation — filtered by chosen determinant */}
        {showScenarioSelect && (() => {
          const matching = ambulanceScenarios.filter(s => s.nature_code === selectedProtocolId);
          const others = ambulanceScenarios.filter(s => s.nature_code !== selectedProtocolId);
          const renderScenario = (s) => (
            <button
              key={s.id}
              onClick={() => { setSelectedScenario(s); setShowScenarioSelect(false); }}
              className="w-full text-left p-3 rounded-lg border-2 border-gray-100 hover:border-amber-300 hover:bg-amber-50 transition-all"
              data-testid={`scenario-${s.id}`}
            >
              <h4 className="text-sm font-semibold text-gray-800">{s.title}</h4>
              <p className="text-xs text-gray-500 mt-0.5">{s.description?.slice(0, 100)}...</p>
            </button>
          );
          return (
          <div className="mt-6">
            <Card className="glass border-0">
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base" style={{ color: '#002768' }}>Scénarios — {protocol?.title || 'ce déterminant'}</CardTitle>
                  <button onClick={() => { setShowScenarioSelect(false); setShowAllScenarios(false); }} className="text-gray-400 hover:text-gray-600 text-xs">
                    <ChevronLeft className="inline w-4 h-4" /> Retour
                  </button>
                </div>
              </CardHeader>
              <CardContent className="pt-0 px-4 pb-4 space-y-2">
                {matching.length > 0 ? (
                  matching.map(renderScenario)
                ) : (
                  <p className="text-xs text-gray-500 italic py-2" data-testid="no-matching-scenarios">
                    Aucun scénario spécifique pour ce déterminant pour l'instant.
                  </p>
                )}
                {others.length > 0 && (
                  <div className="pt-2 border-t border-gray-100">
                    <button
                      onClick={() => setShowAllScenarios(v => !v)}
                      className="text-xs font-semibold text-gray-500 hover:text-gray-700"
                      data-testid="toggle-other-scenarios-btn"
                    >
                      {showAllScenarios ? '▾ Masquer' : '▸ Afficher'} les autres scénarios ({others.length})
                    </button>
                    {showAllScenarios && (
                      <div className="space-y-2 mt-2">
                        <p className="text-[11px] text-amber-600 italic">⚠ Ces scénarios ne correspondent pas au déterminant choisi — le questionnement du protocole risque de ne pas s'appliquer.</p>
                        {others.map(renderScenario)}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          );
        })()}
      </div>
    );
  }

  const currentPhase = protocol.phases[currentPhaseIdx];
  const onPreArrival = !completed && currentPriority === 1 && currentPhaseIdx >= protocol.phases.length;
  const progress = Math.min(100, ((currentPhaseIdx + 1) / protocol.phases.length) * 100);

  return (
    <div className="max-w-4xl mx-auto py-4 px-4" data-testid="health-session">
      {/* Header with priority indicator */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Heart className="w-5 h-5" style={{ color: '#DC2626' }} />
          <div>
            <h2 className="text-lg font-bold" style={{ color: '#002768' }}>{protocol.title}</h2>
            <span className="text-xs text-gray-400">{mode === 'guided' ? 'Mode Guidé' : 'Mode Simulation'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Priority badge */}
          <div className="px-3 py-1.5 rounded-full text-xs font-bold" style={{ backgroundColor: PRIORITY_CONFIG[currentPriority].bg, color: PRIORITY_CONFIG[currentPriority].color }}>
            {PRIORITY_CONFIG[currentPriority].label}
          </div>
          {redFlags.length > 0 && (
            <div className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">
              {redFlags.length} alerte{redFlags.length > 1 ? 's' : ''}
            </div>
          )}
          {!completed && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Terminer l\'appel maintenant ? Le résumé de session sera généré.')) {
                  completeSession();
                }
              }}
              className="ml-2 px-3 py-1.5 rounded-lg text-xs font-bold text-white hover:opacity-90 transition"
              style={{ backgroundColor: '#DC2626' }}
              data-testid="end-call-button"
              title="Raccrocher l'appel — comme en situation réelle"
            >
              📞 Terminer l'appel
            </button>
          )}
        </div>
      </div>

      {/* Persistent P1/P0 banner — ambulance dispatched, keep questioning */}
      {!completed && currentPriority === 1 && !showPriorityAlert && (
        <div
          className="mb-3 p-3 rounded-xl border-2 flex items-center gap-3 animate-in fade-in"
          style={{ borderColor: '#DC2626', backgroundColor: '#FEF2F2' }}
          data-testid="priority-1-banner"
        >
          <div className="text-2xl">🚨</div>
          <div className="flex-1">
            <div className="text-sm font-bold text-red-700">
              PRIORITÉ {currentPriority} ACTIVE — Ambulance envoyée
            </div>
            <div className="text-xs text-red-600">
              Continuez le questionnement : informations pour paramédics, consignes pré-arrivée, soutien au témoin. Raccrochez seulement à l'arrivée des secours.
            </div>
          </div>
          {!onPreArrival && (
            <button
              type="button"
              onClick={() => { setShowPriorityAlert(false); setCurrentPhaseIdx(protocol.phases.length); }}
              className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold text-white hover:opacity-90 transition"
              style={{ backgroundColor: '#DC2626' }}
              data-testid="goto-pre-arrival-btn"
            >
              🚑 Consignes pré-arrivée →
            </button>
          )}
        </div>
      )}

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
        <div className="h-2 rounded-full transition-all duration-500" style={{ width: completed ? '100%' : progress + '%', backgroundColor: PRIORITY_CONFIG[currentPriority].color }} />
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Left: Phase navigation - clickable for visited phases */}
        <div className="col-span-1">
          <div className="space-y-1 max-h-[420px] overflow-y-auto">
          {protocol.phases.map((phase, idx) => {
            const isActive = idx === currentPhaseIdx && !completed;
            const isDone = (idx < maxReachedIdx || completed) && !isActive;
            const canNavigate = idx <= maxReachedIdx && idx !== currentPhaseIdx && !completed;
            return (
              <div key={phase.id}
                onClick={canNavigate ? () => { setCurrentPhaseIdx(idx); setShowPriorityAlert(false); } : undefined}
                className={`flex items-center gap-2 py-1.5 px-2 rounded-lg text-xs transition-all ${canNavigate ? 'cursor-pointer hover:ring-2 hover:ring-green-300' : ''}`}
                style={{ backgroundColor: isActive ? '#EFF6FF' : isDone ? '#F0FDF4' : 'transparent' }}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold" style={{
                  backgroundColor: isDone ? '#22C55E' : isActive ? '#2E6CF5' : '#E5E7EB',
                  color: isDone || isActive ? 'white' : '#9CA3AF'
                }}>
                  {isDone ? '✓' : phase.number}
                </div>
                <span className={isDone ? 'text-green-700 font-medium' : isActive ? 'text-blue-700 font-semibold' : 'text-gray-400'}>{phase.title}</span>
              </div>
            );
          })}
          {/* Virtual P1 pre-arrival phase — unlocked when P1 triggered */}
          {p1TriggeredAt && !completed && (
            <div
              onClick={() => { setCurrentPhaseIdx(protocol.phases.length); setShowPriorityAlert(false); }}
              className={`flex items-center gap-2 py-1.5 px-2 rounded-lg text-xs transition-all cursor-pointer hover:ring-2 hover:ring-red-300 ${onPreArrival ? '' : ''}`}
              style={{ backgroundColor: onPreArrival ? '#FEF2F2' : 'transparent', border: '1px dashed #FCA5A5' }}
              data-testid="nav-pre-arrival-phase"
            >
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold" style={{ backgroundColor: onPreArrival ? '#DC2626' : '#FEE2E2', color: onPreArrival ? 'white' : '#DC2626' }}>
                🚑
              </div>
              <span className={onPreArrival ? 'text-red-700 font-semibold' : 'text-red-500 font-medium'}>Consignes pré-arrivée &amp; paramédics</span>
            </div>
          )}
          </div>
          {/* Persistent timestamped call notes — visible during every phase */}
          {!completed && (
            <CallNotesPanel notes={callNotes} onAddNote={(n) => setCallNotes(prev => [...prev, n])} getElapsed={getElapsed} />
          )}
        </div>

        {/* Right: Active phase content */}
        <div className="col-span-2" ref={contentRef}>
          {completed ? (
            <SessionSummary
              protocol={protocol}
              allAnswers={allAnswers}
              redFlags={redFlags}
              priority={currentPriority}
              duration={sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0}
              preArrival={p1TriggeredAt ? preArrival : null}
              p1Seconds={p1EndSeconds}
              callNotes={callNotes}
              phaseTimes={phaseTimes}
              onPrint={printSummary}
              onReset={resetSession}
              onAutoEval={sessionId ? () => navigate(`/auto-evaluation/health/${sessionId}`) : undefined}
            />
          ) : showPriorityAlert && currentPriority <= 2 ? (
            <PriorityAlert
              priority={currentPriority}
              protocol={protocol}
              onContinue={() => { setShowPriorityAlert(false); advancePhase(); }}
            />
          ) : onPreArrival ? (
            <PreArrivalPhase
              protocol={protocol}
              p1TriggeredAt={p1TriggeredAt}
              preArrival={preArrival}
              setPreArrival={setPreArrival}
              onEndCall={completeSession}
            />
          ) : currentPhase ? (
            <>
            {currentPhaseIdx < maxReachedIdx && (
              <div className="mb-3 p-2.5 rounded-lg border flex items-center gap-2" style={{ borderColor: '#86EFAC', backgroundColor: '#F0FDF4' }} data-testid="revisit-phase-banner">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span className="text-xs text-green-800 flex-1">Phase déjà répondue — vos réponses sont conservées. Vous pouvez les modifier ou revenir où vous étiez.</span>
                <button
                  type="button"
                  onClick={() => { setCurrentPhaseIdx(maxReachedIdx); setShowPriorityAlert(false); }}
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold text-white hover:opacity-90"
                  style={{ backgroundColor: '#16A34A' }}
                  data-testid="resume-current-phase-btn"
                >
                  Reprendre où j'étais →
                </button>
              </div>
            )}
            <Card className="glass border-0">
              <CardHeader className="py-3 px-4">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4" style={{ color: '#002768' }} />
                  <CardTitle className="text-base" style={{ color: '#002768' }}>
                    Phase {currentPhase.number} — {currentPhase.title}
                  </CardTitle>
                </div>
                {currentPhase.objective && <p className="text-xs text-gray-500 mt-1">{currentPhase.objective}</p>}
              </CardHeader>
              <CardContent className="pt-0 px-4 pb-4">
                {currentPhase.type === 'info_gathering' && (
                  <InfoGatheringPhase
                    phase={currentPhase}
                    onComplete={advancePhase}
                    answers={allAnswers}
                    setAnswers={setAllAnswers}
                  />
                )}
                {currentPhase.type === 'open_question' && (
                  <OpenQuestionPhase key={currentPhase.id} phase={currentPhase} initial={allAnswers[currentPhase.id]} onComplete={(noted) => { setAllAnswers(prev => ({ ...prev, [currentPhase.id]: noted })); advancePhase(); }} />
                )}
                {currentPhase.type === 'decision' && (
                  <DecisionPhase key={currentPhase.id} phase={currentPhase} selected={allAnswers[currentPhase.id]} onAnswer={handleDecisionAnswer} />
                )}
                {currentPhase.type === 'multi_question' && (
                  <MultiQuestionPhase key={currentPhase.id} phase={currentPhase} initial={allAnswers[currentPhase.id]} onComplete={handleMultiComplete} />
                )}
                {currentPhase.type === 'comparison' && (
                  <ComparisonPhase key={currentPhase.id} phase={currentPhase} selected={allAnswers[currentPhase.id]} onAnswer={handleComparisonAnswer} />
                )}
              </CardContent>
            </Card>
            </>
          ) : null}

          {/* CPR Quick Guide button — shown for all phases of guided mode (not on completed screen) */}
          {!completed && (currentPhase || onPreArrival) && (
            <button
              type="button"
              onClick={() => setShowCPRGuide(true)}
              className={`mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white text-sm font-bold shadow transition-all ${cprAlertActive ? 'animate-pulse ring-4 ring-red-300' : ''}`}
              style={{ backgroundColor: '#DC2626' }}
              data-testid="health-open-cpr-guide-button"
              title="Guide rapide pour assister un RCR par téléphone"
            >
              <HeartPulse className="w-4 h-4" />
              {cprAlertActive ? '🫀 GUIDE RCR — URGENCE' : '🫀 Guide RCR (assistance téléphonique)'}
            </button>
          )}

          {/* EpiPen quick reference — Réaction allergique only */}
          {!completed && (currentPhase || onPreArrival) && protocol?.id === 'reaction_allergique' && (
            <button
              type="button"
              onClick={() => setShowEpiPenGuide(true)}
              className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white text-sm font-bold shadow transition-all hover:brightness-110"
              style={{ backgroundColor: '#EA580C' }}
              data-testid="health-open-epipen-guide-button"
              title="Pense-bête : guider un appelant avec un EpiPen"
            >
              💉 Pense-bête EpiPen (guider l&apos;appelant)
            </button>
          )}
        </div>
      </div>

      <CPRQuickGuide open={showCPRGuide} onClose={() => setShowCPRGuide(false)} />
      <EpiPenQuickGuide open={showEpiPenGuide} onClose={() => setShowEpiPenGuide(false)} />
    </div>
  );
};

export default Health;
